/**
 * Tracks list — one card per registered track, two per row on a wide screen.
 *
 * Each card states whether anybody can actually USE the copilot on that track
 * (HUB-087, D28 of #218). The HUB-077 chat gate refuses every principal
 * without `track#read` and short-circuits for system admins, so a track with
 * no membership tuples serves a working copilot to the admin reading this page
 * and 403s every other user. The card says so in destructive colour, and the
 * footer offers the one-click fix: a `viewer@system:hub` tuple, the everyone-
 * subject wildcard the authz engine honors (`authz/engine.ts:109-120`).
 *
 * That grant is a normal tuple written through the existing `POST /relations`
 * — no new hub surface, revocable from here or from Permissions.
 */
import { useCallback, useEffect, useState } from 'react';
import { useList } from '@refinedev/core';
import { Link } from 'react-router';
import { AlertCircle, Globe } from 'lucide-react';
import type { RelationListItem, TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { trackLabel } from '@verybigthings/trackframe-shared/tracks';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TableSearch } from '@/components/TableSearch';
import { PageHeader } from '@/components/PageHeader';
import { hub } from '@/lib/hub-client';
import { fmtDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { permissionsHrefForObject } from '../relations/permissions-link';
import {
  deriveTrackAccess,
  isLockedOut,
  isOpenToAll,
  NO_ACCESS,
  openToAllBadgeLabel,
  openToAllTier,
  setTrackOpenToAll,
  type TrackAccessState,
} from './track-access';

/** Client-side name/URL match — the list is small and already fully loaded. */
export function matchesTrackSearch(track: TfTrackRow, search: string): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return [track.name, track.displayName, track.description, track.apiUrl, track.baseUrl]
    .some((field) => field?.toLowerCase().includes(term));
}

export interface TrackAccessNoticeProps {
  access: TrackAccessState;
  /** Permissions deep link listing this track's grants; omitted when locked out. */
  permissionsHref?: string;
}

/** `N people have access`, linked to the grants it is counting. */
function PeopleCount({ count, href }: { count: number; href?: string }) {
  const label = `${count === 1 ? '1 person has' : `${count} people have`} access`;
  if (!href) return <>{label}</>;
  return (
    <Link to={href} className="underline underline-offset-2 hover:text-foreground">
      {label}
    </Link>
  );
}

/** The one line on the card that answers "can anyone actually use this?". */
export function TrackAccessNotice({ access, permissionsHref }: TrackAccessNoticeProps) {
  if (isLockedOut(access)) {
    return (
      <p className="flex items-start gap-2 text-sm font-medium text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          No one except system admins can read this track — every other user is refused the copilot.
        </span>
      </p>
    );
  }
  if (isOpenToAll(access)) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          Open to all hub users
          {/* NOT "N people have access" — everyone has access; these are the
              people who additionally hold a grant of their own. */}
          {access.peopleCount > 0 && (
            <>
              {' · '}
              {permissionsHref ? (
                <Link
                  to={permissionsHref}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {access.peopleCount} also granted individually
                </Link>
              ) : (
                `${access.peopleCount} also granted individually`
              )}
            </>
          )}
        </span>
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      <PeopleCount count={access.peopleCount} href={permissionsHref} />
    </p>
  );
}

export interface TrackCardProps {
  track: TfTrackRow;
  access: TrackAccessState;
  busy: boolean;
  onToggleOpenToAll: () => void;
}

export function TrackCard({ track, access, busy, onToggleOpenToAll }: TrackCardProps) {
  const tier = openToAllTier(access);
  return (
    <Card className={cn('flex h-full flex-col', isLockedOut(access) && 'border-destructive/50')}>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base">{trackLabel(track)}</CardTitle>
          {tier && (
            // A wildcard that grants WRITE to everyone is a different posture
            // from the read-only one this page offers — badge it louder.
            <Badge variant={tier === 'viewer' ? 'muted' : 'destructive'}>
              {openToAllBadgeLabel(tier)}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{track.description ?? 'No description.'}</p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        <dl className="space-y-1 text-xs">
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-muted-foreground">API</dt>
            <dd className="truncate font-mono">{track.apiUrl}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-muted-foreground">Frontend</dt>
            <dd className="truncate font-mono">
              {track.baseUrl ? (
                <a href={track.baseUrl} target="_blank" rel="noreferrer" className="underline">
                  {track.baseUrl}
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-muted-foreground">Updated</dt>
            <dd>{fmtDate(track.updatedAt)}</dd>
          </div>
        </dl>

        {/* No link when locked out — the destination would list nothing. */}
        <TrackAccessNotice
          access={access}
          permissionsHref={
            isLockedOut(access) ? undefined : permissionsHrefForObject('track', track.id)
          }
        />

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          <Link
            to={`/admin/tracks/show/${track.id}`}
            className={cn(buttonVariants({ size: 'sm', variant: 'ghost' }))}
          >
            View
          </Link>
          <Link
            to={`/admin/tracks/edit/${track.id}`}
            className={cn(buttonVariants({ size: 'sm', variant: 'outline' }))}
          >
            Edit
          </Link>
          <Button
            size="sm"
            variant={tier ? 'outline' : 'default'}
            disabled={busy}
            onClick={onToggleOpenToAll}
          >
            {/* "…access…", not "…viewer…": the revoke may be clearing a member
                or admin wildcard written from the Permissions screen. */}
            {tier ? 'Remove access for all users' : 'Add viewer to all hub users'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function TracksList() {
  const { query } = useList<TfTrackRow>({ resource: 'tracks', pagination: { mode: 'off' } });
  const [search, setSearch] = useState('');
  const [access, setAccess] = useState<Map<string, TrackAccessState>>(new Map());
  const [pending, setPending] = useState<TfTrackRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ONE call for every track's tuples — `objectType=track` narrows server-side
  // and the fold happens here, so the page never issues a request per card.
  const loadAccess = useCallback(async () => {
    try {
      const res = await hub.relationsList({
        query: { objectType: 'track', includeStructural: false, limit: 500 },
      });
      setAccess(deriveTrackAccess(res.relations as RelationListItem[]));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  const confirmToggle = async () => {
    if (!pending) return;
    const track = pending;
    const state = access.get(track.id) ?? NO_ACCESS;
    setBusyId(track.id);
    setError(null);
    try {
      // ONE helper for this card and the track page's Access tab (HUB-109
      // D7); it deletes every wildcard relation when closing.
      await setTrackOpenToAll(hub, track.id, !isOpenToAll(state));
      await loadAccess();
      setPending(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const tracks = (query.data?.data ?? []).filter((track) => matchesTrackSearch(track, search));
  const pendingState = pending ? (access.get(pending.id) ?? NO_ACCESS) : NO_ACCESS;
  const pendingOpen = isOpenToAll(pendingState);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tracks"
        description="Registered apps that the hub copilots into."
        actions={[{ label: 'Register track', to: '/admin/tracks/create' }]}
      />

      <TableSearch value={search} onChange={setSearch} placeholder="Filter by name or URL…" />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading tracks…</p>
      ) : tracks.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tracks match.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {tracks.map((track) => (
            <TrackCard
              key={track.id}
              track={track}
              access={access.get(track.id) ?? NO_ACCESS}
              busy={busyId === track.id}
              onToggleOpenToAll={() => setPending(track)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pendingOpen ? 'Remove access for all users?' : 'Add viewer to all hub users?'}
        description={
          pendingOpen
            ? `Removes the all-users ${pendingState.openToAllRelations.join(' and ')} grant on ${pending ? trackLabel(pending) : ''}. Every signed-in user loses that access unless they hold their own grant; users with no other grant will be refused the copilot.`
            : `Every signed-in hub user gets read access to ${pending ? trackLabel(pending) : ''} and can use the copilot on it. Write actions still require member or admin.`
        }
        confirmLabel={pendingOpen ? 'Remove' : 'Add viewer for all'}
        destructive={pendingOpen}
        busy={busyId !== null}
        onConfirm={() => void confirmToggle()}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
